import React, { Suspense } from "react";
import API from "./TimeEditAPI";
import { MillenniumDate, MillenniumDateTime } from "@timeedit/millennium-time";
import DataStore from "./DataStore";
import Log from "./Log";
import Language from "../lib/Language";
import _ from "underscore";
import { EAppId } from "@timeedit/types/lib/enums/appIds.enum";

import { asyncWithLDProvider } from "launchdarkly-react-client-sdk";

import * as Sentry from "@sentry/react";
import { ViewList } from "../models/ViewList";
import { Menu } from "../models/Menu";
import { App } from "../models/App";
import AppComponent from "../components/App";
import ErrorBoundary from "../components/ErrorBoundary";
import {
    AuthWrapper,
    getAccessToken,
} from "@timeedit/ui-components/components/AuthWrapper/AuthWrapper";
import { ConfigProvider } from "@timeedit/ui-components/antd";
import { TRefExtid } from "../types/api/shared";
import { IGetStartupDataResponse } from "../types/api/APIResponseTypes";
import { Scratchpad } from "../models/Scratchpad";
import { registerDevTools } from "./Devtools";
import examCoreAPI from "../components/exam/ExamCoreAPI";
import { TColorDefClass } from "../types/api/APIClassTypes";
import { appRoot } from "../main";
import CoreLDInitializer from "../components/CoreLDInitializer";
import {
    EventInterceptionPlugin,
    FlagOverridePlugin,
    useLaunchDarklyToolbar,
} from "@launchdarkly/toolbar";
import { setupServerConnection, TServerEnv } from "./MessageDispatcherUtils";
import coreAPI from "../components/preferences/PrefsCoreAPI";
import { ThemeWrapper } from "@timeedit/ui-components/components/ThemeWrapper/ThemeWrapper";
import { Provider as ReduxProvider } from "react-redux";
import { AmBeApiContext } from "@timeedit/schedule-fe-lib/am-be";
import { createTecoreAmBeApiStore } from "./amBeApi";

const flagOverridePlugin = new FlagOverridePlugin();
const eventInterceptionPlugin = new EventInterceptionPlugin();

const getLdClientSideId = (env) => {
    switch (env) {
        case "development":
        case "beta":
            return "65e829cc7386bf105e2e28d5";
        case "staging":
            return "65e829836880eb10b9f4edca";
        case "test":
            return "67ab2b0fbad72e0a5478c6a6";
        // Production
        default:
            return "65a938f0183d141061d386f9";
    }
};

export type TTimeEditGlobals = {
    getCompletePropertyId(): number;
    presentShortcut(arg: string): string;
    isEmailActive?: boolean;
    reservationTextField?: TRefExtid;
    locationTypes?: number[];
    rootType?: number;
    calendarBackgroundTexts?: string[];
    entryPropertyDefinitions: IGetStartupDataResponse["parameters"][0]["propertyDefs"];
    State: DataStore;
    API: typeof API;
    Log: typeof Log;
    Language: typeof Language;
    Locale?: typeof Language;
    MillenniumDate: typeof MillenniumDate;
    MillenniumDateTime: typeof MillenniumDateTime;

    toDate: (dateNumber: number) => string;
    toDateTime: (mts) => string;
    toTime: (mts) => string;
    run: () => Promise<void>;
    logoutUser: (ssoLogoutFn?: () => void) => void;
    /** Set by the exam wrapper, for the plugin and for the console. */
    examAPI?: ReturnType<typeof examCoreAPI>;
    onCreateExam?: (examId?: string, reservationId?: string) => void;

    colorDefs?: TColorDefClass[];
    serverInfo?: IGetStartupDataResponse["parameters"][0]["versionInfo"];
    scratchpad?: Scratchpad;
    prefsAPI?: ReturnType<typeof coreAPI>;
};

const AppWithThemeWrapper: React.FC<{ state: DataStore; env }> = ({ state, env }) => {
    // Initialize toolbar with the same plugin instances
    useLaunchDarklyToolbar({
        flagOverridePlugin, // For flag overrides (SDK Mode only)
        eventInterceptionPlugin, // For event monitoring (works in both modes)
        // Common options
        position: "bottom-right",
        enabled: process.env.NODE_ENV === "development", // disable in production/hosted environments
    });

    const appProps = (state.get() as App).asProps();

    return (
        <Suspense>
            <ThemeWrapper defaultComponentSize={"middle"}>
                <AppComponent {...appProps} env={env} />
            </ThemeWrapper>
        </Suspense>
    );
};

export const TimeEdit: TTimeEditGlobals = (function () {
    const State = new DataStore({});
    const amBeApiStore = createTecoreAmBeApiStore(() => State.get() as App);

    const AUTH_URLS = {
        development: process.env.AUTH_URL ?? "http://localhost:3000",
        beta: "https://auth.timeedit.dev",
        staging: "https://auth.timeedit.io",
        test: "https://auth.test.timeedit.net",
        production: "https://auth.timeedit.net",
    };

    const getEnvFromURL = (): keyof typeof AUTH_URLS => {
        const hostName = window.location.host;
        if (process.env.NODE_ENV === "development") {
            return "development";
        }
        if (
            hostName.endsWith("frontend-preview.timeedit.dev") ||
            hostName.endsWith("timeedit.dev")
        ) {
            return "beta";
        }
        if (hostName.endsWith("timeedit.io")) {
            return "staging";
        }
        if (hostName.endsWith("test.timeedit.net")) {
            return "test";
        }
        if (hostName.endsWith("timeedit.net")) {
            return "production";
        }
        return "production";
    };

    const getAuthURLFromURL = (): string => {
        return AUTH_URLS[getEnvFromURL()];
    };

    const extractSignatureFromPath = (path) => {
        const signature = path.split("/")[0];
        if (!signature) return "";
        return `cloud_${signature}`;
    };

    const renderApp = async function (this: any, LDProvider) {
        const onLogin = (token) => {
            // If URL has no customer signature, redirect using token's customerSignature
            const pathSegments = window.location.pathname.split("/").filter(Boolean);
            // URL:s for preview links have a somewhat different format.
            const isPreview = window.location.href.startsWith(window.PREVIEW_BASE_URL);
            const hasCustomerSignature = isPreview
                ? pathSegments.length >= 2 &&
                  pathSegments[2] === "client" &&
                  pathSegments[1] !== "client"
                : pathSegments.length >= 2 &&
                  pathSegments[1] === "client" &&
                  pathSegments[0] !== "client";
            if (!hasCustomerSignature && token?.customerSignature) {
                const sig = token.customerSignature.replace(/^cloud_/, "");
                const fullUrl = `${window.location.origin}${
                    isPreview ? `/${pathSegments[0]}` : ""
                }/${sig}/client`;
                if (sig) {
                    window.location.replace(fullUrl);
                    return;
                }
            }

            const aToken = getAccessToken();
            setupServerConnection((env: TServerEnv) => {
                API.loginWithSSOToken(aToken, (result) => {
                    if (result[0] && result[0].class === "error") {
                        console.log("Not authenticated", result);
                        TimeEdit.State.update(TimeEdit.State.get(), {
                            token: null,
                            logoutAuth: true,
                        });
                    } else {
                        console.log("SSO login result:", result);
                        if (result.parameters[0] === false) {
                            console.log("Not authenticated");
                            TimeEdit.State.update(TimeEdit.State.get(), {
                                token: null,
                                logoutAuth: true,
                            });
                        } else {
                            State.subscribe(renderApp.bind(this, LDProvider));
                            TimeEdit.State.update(TimeEdit.State.get(), {
                                token,
                                env,
                                logoutAuth: false,
                            });
                        }
                    }
                });
            });
        };

        const onLogout = () => {
            console.log("On logout");
            TimeEdit.State.update(TimeEdit.State.get(), { token: null });
        };

        const { env, token } = State.get()?.asProps?.() ?? {};

        appRoot.render(
            <LDProvider>
                <ConfigProvider>
                    <ErrorBoundary>
                        <AuthWrapper
                            onLoginSuccess={onLogin}
                            onLogout={onLogout}
                            authUrl={getAuthURLFromURL()}
                            appId={EAppId.TE_CORE}
                            customerSignature={extractSignatureFromPath(window.TIMEEDIT_APP_PATH)}
                            enableLogs={true}
                            renderChildrenWhileLoading={false}
                        >
                            <CoreLDInitializer
                                region={env?.serverRegion || ""}
                                customerSignature={TimeEdit.serverInfo?.server.signature ?? ""}
                                token={token}
                            >
                                <ReduxProvider context={AmBeApiContext} store={amBeApiStore}>
                                    <AppWithThemeWrapper state={State} env={env} />
                                </ReduxProvider>
                            </CoreLDInitializer>
                        </AuthWrapper>
                    </ErrorBoundary>
                </ConfigProvider>
            </LDProvider>
        );
    };

    const logoutUser = function (ssoLogoutFn?: () => void) {
        console.log("Logging out using SSO");

        API.logoutUser((result) => {
            if (ssoLogoutFn) {
                ssoLogoutFn();
            }
        });
    };

    const run = async function () {
        if (process.env.NODE_ENV !== "development") {
            Sentry.init({
                dsn: "https://ff17d752ee011c2d3e654725fb456567@o4506875039318016.ingest.us.sentry.io/4506875378008064",
                // Performance Monitoring
                tracesSampleRate: 1.0, //  Capture 100% of the transactions
                // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
                tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
                // Session Replay
                replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
                replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
            });
        } else {
            console.log("Developer mode, not enabling Sentry");
        }

        const menuItems = [
            { id: "views", label: "permission_views", model: new ViewList() },
            { id: "settings", label: "cal_res_side_tab_view_prefs" },
            { id: "layers", label: "nc_drafts_title" },
            { id: "account", label: "nc_account_settings" },
            { id: "info", label: "cal_res_side_tab_res_info" },
            { id: "pinboard", label: "nc_pinboard" },
            { id: "help", label: "menu_help" },
        ];

        const menu = new Menu(menuItems);
        menu.freeze();
        State.update(new App(menu, null));

        const LDProvider = await asyncWithLDProvider({
            clientSideID: getLdClientSideId(getEnvFromURL()),
            context: {
                kind: "uninitialized",
                key: "uninitialized",
            },
            options: {
                plugins: [flagOverridePlugin, eventInterceptionPlugin],
            },
        });

        renderApp(LDProvider);
    };

    const getCompletePropertyId = function () {
        return _.find(TimeEdit.entryPropertyDefinitions, (def) => def?.complete === true)?.id;
    };

    const presentShortcut = (shortcut) => shortcut.replace("mod", Language.get("nc_key_mod_short"));

    return {
        State,
        logoutUser,
        run,
        getCompletePropertyId,
        DEBUG: false,
        presentShortcut,
    };
})() as unknown as TTimeEditGlobals;

if (process.env.NODE_ENV === "development") {
    registerDevTools(TimeEdit);
}
